import { map, tap } from 'rxjs/operators';
import { ErrorHandler, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConfigService } from './config.service';
import { Authentication } from '../../models/authentication';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { LoggedInUser } from '../../models/logged-in-user';
import { RollbarErrorHandler } from 'src/app/rollbar.service';

export interface ResetPasswordPayload {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}

@Injectable()
export class AuthenticationService {
  API_URL = environment.api_url;
  private authenticatedSubject = new BehaviorSubject<boolean>(false);
  private _token: string;
  authenticated = this.authenticatedSubject.asObservable();
  redirectUrl: string;

  constructor(
    private http: HttpClient,
    private router: Router,
    private configService: ConfigService,
    private rollbarErrorHandler: ErrorHandler
  ) {}

  login(credentials: any): Observable<any> {
    return this.http
      .post(this.API_URL + 'login', credentials, { observe: 'response' })
      .pipe(
        tap((response: HttpResponse<Authentication>) => {
          if (response.status === 200) {
            let authentication: Authentication =
              response.body as Authentication;
            this.setUserLoggedIn(authentication);
          }
        })
      );
  }

  requestResetPassword(email: string) {
    return this.http.post<{ success: boolean }>(
      this.API_URL + 'forgot-password',
      { email }
    );
  }

  resetPassword(payload: ResetPasswordPayload) {
    return this.http.post<{ success: boolean }>(
      this.API_URL + 'reset-password',
      payload
    );
  }

  setUserLoggedIn(authentication: Authentication) {
    this.token = authentication.token;
    this.setRollbarUserDetails({
      id: authentication.user.id,
      name: authentication.user.name,
      email: authentication.user.email,
      tenant: authentication.user.user_tenant_id.toString()
    });
    this.authenticatedSubject.next(true);
  }

  private setRollbarUserDetails(user: {
    id: number;
    name: string;
    email: string;
    tenant: string;
  }) {
    (this.rollbarErrorHandler as RollbarErrorHandler).setUserDetails(user);
  }

  getLoggedInUser() {
    return this.configService.getLoggedInUser(this.token).pipe(
      map((loggedInUserResponse: HttpResponse<LoggedInUser>) => {
        if (loggedInUserResponse.status === 200) {
          this.authenticatedSubject.next(true);
        }
        return loggedInUserResponse;
      }),
      tap((response) => {
        this.setRollbarUserDetails({
          id: response.body.user.id,
          name: response.body.user.name,
          email: response.body.user.email,
          tenant: response.body.user.tenant.tenant_ref
        });
      })
    );
  }

  set token(value) {
    this._token = value;
    localStorage.setItem('token', value);
  }

  get token() {
    if (!this._token) {
      this._token = localStorage.getItem('token');
    }
    return this._token;
  }

  notAuthenticated(timeout = false) {
    localStorage.removeItem('token');
    this._token = null;
    this.authenticatedSubject.next(false);
    this.configService.clearConfiguration();
    const params = {};
    if (timeout) {
      params['reason'] = 'timeout';
    }
    // next tick hack needed to prevent an error on navigation to logout
    setTimeout(() => this.configService.userLogout.next(), 0);
    this.router.navigate(['/login', params]);
  }
}
